/**
 * ChatService - Singleton service for managing chat WebSocket connection
 *
 * DATE: 2025-12-19 | Author: IA + Luiz Couto
 *
 * Centralized service that manages the WebSocket connection to UserDO,
 * maintains global state (friends, conversations), and broadcasts events
 * to multiple ConversationWindow instances.
 *
 * Part of the Multi-Window Chat System refactoring.
 */

import { WebSocketConnection } from '../websocket';

// ============================================================================
// TYPES
// ============================================================================

// Friend data structure
export interface Friend {
  netcartasId: number;
  nickname: string;
  isAI: boolean;
  isOnline: boolean;
  lastMessageAt?: number;
}

// Chat message structure
export interface ChatMessage {
  id?: number;
  fromNetcartasId: number;
  toNetcartasId: number;
  message: string;
  createdAt: number;
  read?: boolean;
  /** System messages like "Amizade aceita" */
  isSystem?: boolean;
}

// Conversation summary for friends list
export interface ConversationSummary {
  friendNetcartasId: number;
  friendNickname: string;
  isAI: boolean;
  lastMessage?: string;
  lastMessageAt?: number;
  unreadCount: number;
  isOnline: boolean;
}

// Event types emitted by ChatService
// DATE: 2025-12-21 | Added 'typing' event for typing indicator
// DATE: 2025-12-22 | Added 'friendRequestReceived' for AI Companion friend requests
export type ChatServiceEvent =
  | 'connected'
  | 'disconnected'
  | 'chatReceived'
  | 'typing'           // DATE: 2025-12-21 | Typing indicator from AI companion
  | 'friendOnline'
  | 'friendOffline'
  | 'friendAdded'
  | 'conversationsUpdated'
  | 'messagesLoaded'
  | 'friendsUpdated'
  | 'friendRequestReceived'  // DATE: 2025-12-22 | Friend request from AI Companion
  | 'error';                 // DATE: 2025-12-27 | Error messages from UserDO

// Event handler type
export type ChatEventHandler = (data: any) => void;

// ============================================================================
// SERVER URL
// ============================================================================

const SERVER_URL = import.meta.env.DEV
  ? 'http://localhost:8881'
  : 'https://domino-server.pixlabs.workers.dev';

// ============================================================================
// CHAT SERVICE SINGLETON
// ============================================================================

export class ChatService {
  // Singleton instance
  private static instance: ChatService | null = null;

  // State
  private friends: Friend[] = [];
  private conversations: ConversationSummary[] = [];

  // User info
  private myNetcartasId: number | null = null;
  private myNickname: string = '';

  // WebSocket
  private userWs: WebSocketConnection | null = null;
  private _isConnected: boolean = false;

  // Event emitter - Map of event name to array of handlers
  private eventHandlers: Map<ChatServiceEvent, ChatEventHandler[]> = new Map();

  // Notification sound
  private notificationSound: HTMLAudioElement | null = null;

  // ============================================================================
  // SINGLETON PATTERN
  // ============================================================================

  private constructor() {
    console.log(`💬 [2025-12-19] ChatService: Singleton instance created`);

    // Initialize notification sound for preloading
    try {
      this.notificationSound = new Audio(new URL('../soundfx/chat_notification.mp3', import.meta.url).href);
      this.notificationSound.volume = 0.5;
      this.notificationSound.preload = 'auto';
      console.log(`🔊 [2025-12-19] ChatService: Notification sound loaded`);
    } catch (error) {
      console.warn(`⚠️ [2025-12-19] ChatService: Could not load notification sound:`, error);
    }
  }

  /**
   * Get the singleton instance of ChatService
   */
  public static getInstance(): ChatService {
    if (!ChatService.instance) {
      ChatService.instance = new ChatService();
    }
    return ChatService.instance;
  }

  // ============================================================================
  // CONNECTION MANAGEMENT
  // ============================================================================

  /**
   * Connect to the UserDO WebSocket
   */
  public async connect(netcartasId: number, nickname: string): Promise<void> {
    if (this._isConnected) {
      console.log(`💬 [2025-12-19] ChatService: Already connected`);
      return;
    }

    this.myNetcartasId = netcartasId;
    this.myNickname = nickname;

    console.log(`💬 [2025-12-19] ChatService: Connecting for user ${nickname} (${netcartasId})`);

    try {
      this.userWs = new WebSocketConnection();

      // Setup message handlers before connecting
      this.setupMessageHandlers();

      // Connect to UserDO WebSocket
      const wsUrl = `${SERVER_URL.replace('http', 'ws')}/api/user/${netcartasId}/ws`;
      console.log(`💬 [2025-12-19] ChatService: Connecting to ${wsUrl}`);

      await this.userWs.connect(wsUrl);
      this._isConnected = true;

      // Send connect message to UserDO
      this.userWs.send({
        type: 'connect',
        netcartasId: this.myNetcartasId,
        nickname: this.myNickname
      } as any);

      console.log(`✅ [2025-12-19] ChatService: Connected to UserDO`);

    } catch (error) {
      console.error(`❌ [2025-12-19] ChatService: Connection failed:`, error);
      this._isConnected = false;
      throw error;
    }
  }

  /**
   * Disconnect from the UserDO WebSocket
   */
  public disconnect(): void {
    console.log(`💬 [2025-12-19] ChatService: Disconnecting`);

    if (this.userWs) {
      this.userWs.disconnect();
      this.userWs = null;
    }

    this._isConnected = false;
    this.friends = [];
    this.conversations = [];
    this.myNetcartasId = null;
    this.myNickname = '';

    this.emit('disconnected', {});
  }

  /**
   * Check if connected to UserDO
   */
  public isConnected(): boolean {
    return this._isConnected;
  }

  /**
   * Get current user's netcartasId
   */
  public getMyNetcartasId(): number | null {
    return this.myNetcartasId;
  }

  /**
   * Get current user's nickname
   */
  public getMyNickname(): string {
    return this.myNickname;
  }

  /**
   * Get the server URL
   *
   * DATE: 2025-12-24 | Author: IA + Luiz Couto
   * Epic 1.12: Used by ConversationWindow to create games with AI
   */
  public getServerUrl(): string {
    return SERVER_URL;
  }

  // ============================================================================
  // STATE GETTERS
  // ============================================================================

  /**
   * Get the list of friends
   */
  public getFriends(): Friend[] {
    return [...this.friends];
  }

  /**
   * Get the list of conversations
   */
  public getConversations(): ConversationSummary[] {
    return [...this.conversations];
  }

  /**
   * Get a specific friend by netcartasId
   */
  public getFriend(netcartasId: number): Friend | undefined {
    return this.friends.find(f => f.netcartasId === netcartasId);
  }

  /**
   * Get a specific conversation summary by friendNetcartasId
   */
  public getConversation(friendNetcartasId: number): ConversationSummary | undefined {
    return this.conversations.find(c => c.friendNetcartasId === friendNetcartasId);
  }

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Request messages for a specific friend from the server
   * Messages will be delivered via 'messagesLoaded' event
   */
  public requestMessages(friendNetcartasId: number, limit: number = 50): void {
    if (!this.userWs || !this._isConnected) {
      console.warn(`⚠️ [2025-12-19] ChatService: Cannot request messages - not connected`);
      return;
    }

    console.log(`💬 [2025-12-19] ChatService: Requesting messages for friend ${friendNetcartasId}`);

    this.userWs.send({
      type: 'getMessages',
      friendNetcartasId,
      limit
    } as any);
  }

  /**
   * Send a chat message to a friend
   */
  public sendMessage(targetNetcartasId: number, message: string, isAI: boolean): void {
    if (!this.userWs || !this._isConnected || !this.myNetcartasId) {
      console.warn(`⚠️ [2025-12-19] ChatService: Cannot send message - not connected`);
      return;
    }

    console.log(`💬 [2025-12-19] ChatService: Sending message to ${targetNetcartasId}`);

    this.userWs.send({
      type: 'chat',
      targetNetcartasId,
      message,
      isAI
    } as any);
  }

  /**
   * Mark messages from a friend as read
   */
  public markAsRead(friendNetcartasId: number): void {
    if (!this.userWs || !this._isConnected) {
      console.warn(`⚠️ [2025-12-19] ChatService: Cannot mark as read - not connected`);
      return;
    }

    console.log(`💬 [2025-12-19] ChatService: Marking messages as read for friend ${friendNetcartasId}`);

    this.userWs.send({
      type: 'markRead',
      friendNetcartasId
    } as any);
  }

  /**
   * Remove a pending friend request from the server
   * DATE: 2025-12-22 | Called after accepting/declining a friend request
   */
  public removePendingRequest(saidId: number): void {
    if (!this.userWs || !this._isConnected) {
      console.warn(`⚠️ [2025-12-22] ChatService: Cannot remove pending request - not connected`);
      return;
    }

    console.log(`🗑️ [2025-12-22] ChatService: Removing pending request saidId=${saidId}`);

    this.userWs.send({
      type: 'removePendingRequest',
      saidId
    } as any);
  }

  /**
   * Request updated conversations list from server
   */
  public requestConversations(): void {
    if (!this.userWs || !this._isConnected) {
      console.warn(`⚠️ [2025-12-19] ChatService: Cannot request conversations - not connected`);
      return;
    }

    this.userWs.send({
      type: 'getConversations'
    } as any);
  }

  /**
   * Play notification sound
   */
  public playNotificationSound(): void {
    if (this.notificationSound) {
      this.notificationSound.currentTime = 0;
      this.notificationSound.play().catch(err => {
        console.warn(`⚠️ [2025-12-19] ChatService: Could not play notification sound:`, err);
      });
    }
  }

  // ============================================================================
  // EVENT EMITTER
  // ============================================================================

  /**
   * Subscribe to an event
   */
  public on(event: ChatServiceEvent, handler: ChatEventHandler): void {
    if (!this.eventHandlers.has(event)) {
      this.eventHandlers.set(event, []);
    }
    this.eventHandlers.get(event)!.push(handler);
  }

  /**
   * Unsubscribe from an event
   */
  public off(event: ChatServiceEvent, handler: ChatEventHandler): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Emit an event to all subscribers
   */
  private emit(event: ChatServiceEvent, data: any): void {
    const handlers = this.eventHandlers.get(event);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`❌ [2025-12-19] ChatService: Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  // ============================================================================
  // WEBSOCKET MESSAGE HANDLERS
  // ============================================================================

  /**
   * Setup WebSocket message handlers
   */
  private setupMessageHandlers(): void {
    if (!this.userWs) return;

    // Connected - receive initial state
    this.userWs.on('connected', (msg: any) => {
      console.log(`💬 [2025-12-19] ChatService: Received initial state`);
      if (msg.friends) {
        this.friends = msg.friends;
        this.emit('connected', { friends: this.friends });
        this.emit('friendsUpdated', { friends: this.friends });
      }

      // DATE: 2025-12-22 | Handle pending friend requests on connection
      // Show popup for any requests received while user was offline
      if (msg.pendingRequests && Array.isArray(msg.pendingRequests)) {
        console.log(`🤝 [2025-12-22] ChatService: ${msg.pendingRequests.length} pending friend request(s)`);
        for (const request of msg.pendingRequests) {
          console.log(`🤝 [2025-12-22] ChatService: Pending request from ${request.fromNickname}`);
          this.emit('friendRequestReceived', { request });
        }
      }
    });

    // Chat message received
    this.userWs.on('chatReceived', (msg: any) => {
      console.log(`💬 [2025-12-19] ChatService: Message received from ${msg.fromNickname}`);

      // Auto-add sender to friends list if not present
      this.ensureFriendExists(msg.fromNetcartasId, msg.fromNickname, msg.isAI);

      // Emit event for ConversationWindows to handle
      this.emit('chatReceived', {
        fromNetcartasId: msg.fromNetcartasId,
        fromNickname: msg.fromNickname,
        message: msg.message,
        createdAt: msg.createdAt || Date.now(),
        isAI: msg.isAI,
        isSystem: msg.isSystem
      });

      // Request updated conversations list to refresh unread counts
      this.requestConversations();
    });

    // Friend came online
    this.userWs.on('friendOnline', (msg: any) => {
      const friend = this.friends.find(f => f.netcartasId === msg.netcartasId);
      if (friend) {
        friend.isOnline = true;
        this.emit('friendOnline', { netcartasId: msg.netcartasId });
        this.emit('friendsUpdated', { friends: this.friends });
      }
    });

    // Friend went offline
    this.userWs.on('friendOffline', (msg: any) => {
      const friend = this.friends.find(f => f.netcartasId === msg.netcartasId);
      if (friend) {
        friend.isOnline = false;
        this.emit('friendOffline', { netcartasId: msg.netcartasId });
        this.emit('friendsUpdated', { friends: this.friends });
      }
    });

    // Friend added
    this.userWs.on('friendAdded', (msg: any) => {
      this.friends.push(msg.friend);
      this.emit('friendAdded', { friend: msg.friend });
      this.emit('friendsUpdated', { friends: this.friends });
    });

    // Conversations list
    this.userWs.on('conversations', (msg: any) => {
      this.conversations = msg.conversations;
      this.emit('conversationsUpdated', { conversations: this.conversations });
    });

    // Messages for a conversation
    this.userWs.on('messages', (msg: any) => {
      console.log(`💬 [2025-12-19] ChatService: Messages loaded for friend ${msg.friendNetcartasId}`);
      this.emit('messagesLoaded', {
        friendNetcartasId: msg.friendNetcartasId,
        messages: msg.messages
      });
    });

    // DATE: 2025-12-21 | Typing indicator from AI companion
    this.userWs.on('typing', (msg: any) => {
      console.log(`💬 [2025-12-21] ChatService: Typing indicator from ${msg.fromNickname}: ${msg.isTyping}`);
      this.emit('typing', {
        fromNetcartasId: msg.fromNetcartasId,
        fromNickname: msg.fromNickname,
        isTyping: msg.isTyping
      });
    });

    // DATE: 2025-12-22 | Friend request received from AI Companion
    // This triggers a popup in the game UI to accept/decline the request
    this.userWs.on('friendRequestReceived', (msg: any) => {
      console.log(`🤝 [2025-12-22] ChatService: Friend request received from ${msg.request?.fromNickname}`);
      this.emit('friendRequestReceived', {
        request: msg.request
      });
    });

    // Disconnected
    this.userWs.on('disconnected', () => {
      console.log(`💬 [2025-12-19] ChatService: Disconnected`);
      this._isConnected = false;
      this.emit('disconnected', {});
    });

    // DATE: 2025-12-27 | Error handler - supports both old and new formats
    // Old format: { type: 'error', message: '...' }
    // New format: { type: 'error', payload: { code: '...', message: '...' } }
    this.userWs.on('error', (msg: any) => {
      // Extract message from either format
      const errorMessage = msg.message || msg.payload?.message || 'Erro desconhecido';
      const errorCode = msg.payload?.code || 'UNKNOWN';

      console.error(`❌ [2025-12-27] ChatService: Error received [${errorCode}]: ${errorMessage}`);
      this.emit('error', {
        code: errorCode,
        message: errorMessage
      });
    });
  }

  /**
   * Ensure a friend exists in the friends list
   * Used when receiving a message from someone not yet in the list
   */
  private ensureFriendExists(netcartasId: number, nickname: string, isAI: boolean): void {
    const existingFriend = this.friends.find(f => f.netcartasId === netcartasId);
    if (!existingFriend) {
      console.log(`💬 [2025-12-19] ChatService: Adding friend ${nickname} to list`);
      const newFriend: Friend = {
        netcartasId,
        nickname,
        isAI: isAI || false,
        isOnline: true, // Assume online since they just sent a message
        lastMessageAt: Date.now()
      };
      this.friends.push(newFriend);
      this.emit('friendAdded', { friend: newFriend });
      this.emit('friendsUpdated', { friends: this.friends });
    }
  }
}
